import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  AssignStakeholdersDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";
import { AxiosError, AxiosResponse } from "axios";

import { roleGuard } from "@api";
import { doubleMaterialityQuestionsApi } from "@api/services";

export interface AssignStakeholdersResultDto {
  assigned: number;
  skipped: number;
}

const assignStakeholders = async (params: {
  questionId: string;
  payload: AssignStakeholdersDto;
}): Promise<AssignStakeholdersResultDto> => {
  const { questionId, payload } = params;
  const response =
    (await doubleMaterialityQuestionsApi.doubleMaterialityQuestionsControllerAssignStakeholder(
      questionId,
      payload,
    )) as AxiosResponse<AssignStakeholdersResultDto>;

  return response.data;
};

export const useAssignStakeholders = (
  user: UserProfileDto | null,
): {
  assignStakeholders: (params: {
    questionId: string;
    payload: AssignStakeholdersDto;
  }) => void;
  assignStakeholdersAsync: (params: {
    questionId: string;
    payload: AssignStakeholdersDto;
  }) => Promise<AssignStakeholdersResultDto>;
  assignStakeholdersEnabled: boolean;
  isPendingAssignStakeholders: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = roleGuard(user, [UserRole.Owner, UserRole.Manager]);
  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: enabled ? assignStakeholders : undefined,
    onError: (error: AxiosError) => {
      const status = error.response?.status;
      const message =
        (error.response?.data as { message?: string })?.message || error.message;

      if (status === 400) {
        enqueueSnackbar(
          message ||
            t(
              "doubleMateriality.assignStakeholders.internalToExternalError",
              "Internal stakeholders cannot be assigned to external questions",
            ),
          { variant: "error" },
        );
      } else {
        enqueueSnackbar(
          message ||
            t("common.errors.generic", "An error occurred. Please try again."),
          { variant: "error" },
        );
      }
    },
  });

  return {
    assignStakeholders: mutate,
    assignStakeholdersAsync: mutateAsync,
    assignStakeholdersEnabled: enabled,
    isPendingAssignStakeholders: isPending,
  };
};
